import { useEffect, useMemo, useRef, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Check, ChevronDown, ChevronUp, ClipboardCopy, Cloud, ExternalLink, FileText, FileType2, Layers, Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import ExportButtons from "./ExportButtons";
import CopyLessonButton from "./CopyLessonButton";
import LessonRefineChat from "./LessonRefineChat";
import { markdownComponents } from "./LessonDocument";
import StudentMaterialsSection from "./StudentMaterialsSection";
import { driveStatus, generateActivity, getSkillsBreakdown, listUnitLessons, patchUnitLessonSkill, resumeGenerateJob, saveUnitToDrive } from "../lib/api";
import UnitSummativeSection from "./UnitSummativeSection";

// Distinguish a genuine generation failure (the backend rejected the
// request) from a transient timeout / network blip while the backend is
// still chewing on a 60-90s Claude call. In the latter case we should
// NOT show a red error toast — the lesson will still land in the
// teacher's Library when it's ready. Mirrors the LessonInProgressBanner
// "taking a little longer than usual" copy used by the main generator.
const isLikelyStillCooking = (err) => {
  if (!err) return false;
  // No HTTP response at all = network blip / ingress timeout / browser
  // gave up before the long-running call finished.
  if (!err.response) return true;
  // 502/504 from the ingress while the backend is still processing.
  const status = err.response.status;
  return status === 502 || status === 504;
};

const STILL_COOKING_TITLE = "This one is taking a little longer than usual.";
const STILL_COOKING_BODY =
  "Your lesson is still generating — it will appear in your Library when it's ready. You can stay here or navigate away; it won't affect the result.";

const meta = (inputs, lessonCount) => {
  if (!inputs) return [];
  // skill_focus is intentionally NOT here — the transferable-skills array
  // renders the skill pills (see `tags` below). Rendering both produced a
  // duplicate joined pill alongside the per-skill pills (Jun 2026 defect).
  const order = ["grade", "subject", "time", "activity_type"];
  return order
    .map((k) => {
      if (!inputs[k]) return null;
      // A unit spans multiple lessons, so the TIME chip must reflect the
      // whole unit ("6 × 45 minutes"), not the bare per-lesson time (Jun
      // 2026 defect: a 6-lesson unit showed "45 minutes"). Uses the unit's
      // own stored lesson_count; falls back to the raw value only when the
      // count is genuinely absent.
      if (k === "time" && Number.isInteger(lessonCount) && lessonCount > 0) {
        return { key: k, value: `${lessonCount} × ${inputs[k]}` };
      }
      return { key: k, value: inputs[k] };
    })
    .filter(Boolean);
};

// Split unit markdown into:
//   { preface: string, lessons: [{ title, body, prompt }], suffix: string }
// `preface` is everything BEFORE the first "### Lesson" header.
// `suffix` is everything AFTER the last lesson section ends — i.e., starting
// from the next `## ` heading (Differentiation, Summative, etc.).
const splitUnit = (md) => {
  if (!md) return { preface: "", lessons: [], suffix: "" };
  const lines = md.split("\n");
  const lessonStartRe = /^###\s+Lesson\s+(\d+)\s*[—\-:]?\s*(.*)$/i;
  // Find the indexes of each "### Lesson" header AND the next "## " (h2) header,
  // which marks the end of the lesson-by-lesson section.
  const lessonStarts = [];
  let suffixStart = -1;
  for (let i = 0; i < lines.length; i += 1) {
    if (lessonStartRe.test(lines[i])) {
      lessonStarts.push(i);
    } else if (lessonStarts.length > 0 && /^##\s+/.test(lines[i])) {
      suffixStart = i;
      break;
    }
  }
  if (lessonStarts.length === 0) {
    return { preface: md, lessons: [], suffix: "" };
  }
  let preface = lines.slice(0, lessonStarts[0]).join("\n").trim();
  // Strip a trailing `## Lesson-by-Lesson Overview` (or similar) heading
  // from the preface — the UnitDocument lessons section adds its own.
  preface = preface.replace(/\n*##\s+Lesson[-\s]?by[-\s]?Lesson\s+Overview\s*$/i, "").trim();
  const suffix =
    suffixStart >= 0 ? lines.slice(suffixStart).join("\n").trim() : "";

  const lessons = [];
  for (let li = 0; li < lessonStarts.length; li += 1) {
    const start = lessonStarts[li];
    const end =
      li + 1 < lessonStarts.length
        ? lessonStarts[li + 1]
        : suffixStart >= 0
          ? suffixStart
          : lines.length;
    const block = lines.slice(start, end).join("\n").trim();
    // Match against the FIRST line only — block.match with `^...$` on a
    // multi-line string fails without the /m flag and yields "Lesson" only.
    const headerMatch = lines[start].match(lessonStartRe);
    const title = headerMatch ? `Lesson ${headerMatch[1]} — ${(headerMatch[2] || "").trim()}` : "Lesson";

    // Extract the → Prompt: line. The lesson body shown to the teacher will
    // have that line REMOVED — the prompt is shown inside a special card.
    const promptLineRe = /^[-*]?\s*\*?\*?→\s*Prompt:\*?\*?\s*["“]?(.*)$/m;
    const promptMatch = block.match(promptLineRe);
    let prompt = "";
    // Drop the `### Lesson N — …` heading from the body — the card renders it
    // as its own h3 already, so leaving it in causes a duplicate title.
    let body = lines.slice(start + 1, end).join("\n").trim();
    if (promptMatch) {
      // Strip the bullet + → Prompt: line and any continuation lines that are
      // part of the same quoted string (rare — Claude usually keeps it one-line).
      const idx = body.indexOf(promptMatch[0]);
      if (idx >= 0) {
        const before = body.slice(0, idx).trimEnd();
        const afterStart = idx + promptMatch[0].length;
        const after = body.slice(afterStart).trimStart();
        body = (before + (after ? "\n\n" + after : "")).trim();
      }
      // Strip trailing close-quote / period if present.
      prompt = promptMatch[1].trim().replace(/["“”]$/, "").trim();
    }

    lessons.push({ title, body, prompt });
  }
  return { preface, lessons, suffix };
};

const GenerateLessonCard = ({
  prompt,
  lessonIndex,
  unitInputs,
  parentUnitId,
  unitLesson,
  focusStore,
  unitSkillSets,
  onSkillChange,
  status,
  onStatusChange,
}) => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [editedPrompt, setEditedPrompt] = useState(prompt || "");
  const [savingSkill, setSavingSkill] = useState(false);

  const busy = status?.busy || false;
  const generated = status?.generated || null;

  if (!prompt) return null;

  // Store selection this lesson carries into its generation call. Every unit
  // lesson is assigned a skill + one focus area (no exemption), so this is set
  // whenever the record has both. If the proposer left it unassigned, the card
  // shows "Choose a skill" and the teacher picks before generating.
  const skillLookforSelections =
    unitLesson && unitLesson.skill_id && unitLesson.focus_area_number
      ? [{ skill_id: unitLesson.skill_id, lookfor_number: unitLesson.focus_area_number }]
      : [];

  const handleGenerate = async () => {
    if (busy || generated) return;
    onStatusChange?.({ busy: true, generated: null });
    const toastId = toast.loading("Drafting this lesson… a minute or two.", {
      description: "Writing the full plan for you. You can keep scrolling.",
    });
    let capturedJobId = null;
    try {
      const activity = await generateActivity(
        {
          prompt: editedPrompt || prompt,
          grade: unitInputs?.grade || "",
          subject: unitInputs?.subject || "",
          skill_focus: unitInputs?.skill_focus || "",
          time: unitInputs?.time || "",
          activity_type: "Full lesson",
          output_type: "lesson",
          parent_unit_id: parentUnitId || undefined,
          parent_lesson_index: parentUnitId ? lessonIndex : undefined,
          skill_lookfor_selections: skillLookforSelections,
          lookfor_grade: unitInputs?.lookfor_grade || "",
        },
        { onJobStarted: (jid) => { capturedJobId = jid; } },
      );
      onStatusChange?.({ busy: false, generated: activity });
      toast.success("Lesson ready", { id: toastId });
    } catch (e) {
      if (isLikelyStillCooking(e) && capturedJobId) {
        // Background-resume — keep the card in "busy" so it auto-swaps
        // to the green generated state when Claude finishes, without a
        // page refresh.
        toast.info(STILL_COOKING_TITLE, {
          id: toastId,
          description: STILL_COOKING_BODY,
          duration: 8000,
        });
        resumeGenerateJob(capturedJobId)
          .then((activity) => {
            onStatusChange?.({ busy: false, generated: activity });
            toast.success("Lesson ready — it landed automatically.");
          })
          .catch(() => {
            onStatusChange?.({ busy: false, generated: null });
            toast.info(
              "Still working in the background — check My Library in a minute.",
              {
                action: {
                  label: "Open Library",
                  onClick: () => {
                    window.location.href = "/dashboard?tab=library";
                  },
                },
              },
            );
          });
      } else {
        onStatusChange?.({ busy: false, generated: null });
        if (isLikelyStillCooking(e)) {
          toast.info(STILL_COOKING_TITLE, {
            id: toastId,
            description: STILL_COOKING_BODY,
            duration: 10000,
            action: {
              label: "Open Library",
              onClick: () => {
                window.location.href = "/dashboard?tab=library";
              },
            },
          });
        } else {
          toast.error(e?.message || "Couldn't generate this lesson.", { id: toastId });
        }
      }
    }
  };

  // After generation — show a compact "Open lesson" card that links to
  // the full lesson workspace. The lesson lives inside the unit (it has
  // a parent_unit_id stamp so the Library filters it out); the unit is
  // the one place the teacher comes to access it.
  if (generated) {
    return (
      <div
        data-testid={`unit-lesson-generated-${lessonIndex}`}
        className="mt-4 rounded-xl border border-[#96ded1] bg-white p-4 md:p-5"
      >
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#1c2d3d]/70 inline-flex items-center gap-2">
              <Sparkles className="h-3 w-3 text-[#1c2d3d]" />
              Lesson drafted
            </p>
            {generated.activity_name && (
              <p className="mt-1 font-extrabold text-[#1c2d3d] tracking-tight truncate">
                {generated.activity_name}
              </p>
            )}
          </div>
          <Link
            to={`/lesson/${generated.id}`}
            data-testid={`unit-lesson-open-${lessonIndex}`}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1c2d3d] text-white font-bold text-sm hover:bg-[#0f1c28]"
          >
            <ExternalLink className="h-4 w-4" />
            Open lesson
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid={`unit-lesson-prompt-${lessonIndex}`}
      className="mt-4 rounded-lg border border-[#96ded1]/60 bg-[#96ded1]/15 p-4"
    >
      {unitSkillSets && Object.keys(unitSkillSets).length > 0 && (
        <div
          data-testid={`unit-lesson-skill-row-${lessonIndex}`}
          className="mb-3 flex flex-wrap items-center gap-2 text-xs"
        >
          <span className="font-bold uppercase tracking-[0.14em] text-[#1c2d3d]/60">Skill</span>
          <select
            value={unitLesson?.skill_id || ""}
            disabled={busy || savingSkill}
            data-testid={`unit-lesson-skill-select-${lessonIndex}`}
            onChange={async (e) => {
              const v = e.target.value;
              if (!v) return;
              setSavingSkill(true);
              try {
                const first = (unitSkillSets[v]?.lookfors || [])[0]?.number ?? null;
                await onSkillChange?.(lessonIndex + 1, { skill_id: v, focus_area_number: first });
              } finally { setSavingSkill(false); }
            }}
            className="rounded-md border border-[#1c2d3d]/20 bg-white px-2 py-1 font-semibold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
          >
            {!unitLesson?.skill_id && <option value="">Choose a skill…</option>}
            {Object.entries(unitSkillSets).map(([sid, s]) => (
              <option key={sid} value={sid}>{s.skill_name}</option>
            ))}
          </select>
          {unitLesson?.skill_id && (
            <>
              <span className="font-bold uppercase tracking-[0.14em] text-[#1c2d3d]/60">Focus area</span>
              <select
                value={unitLesson?.focus_area_number || ""}
                disabled={busy || savingSkill}
                data-testid={`unit-lesson-focus-select-${lessonIndex}`}
                onChange={async (e) => {
                  setSavingSkill(true);
                  try {
                    await onSkillChange?.(lessonIndex + 1, {
                      skill_id: unitLesson.skill_id,
                      focus_area_number: parseInt(e.target.value, 10),
                    });
                  } finally { setSavingSkill(false); }
                }}
                className="rounded-md border border-[#1c2d3d]/20 bg-white px-2 py-1 font-semibold text-[#1c2d3d] focus:outline-none focus:ring-2 focus:ring-[#96ded1] max-w-[16rem]"
              >
                {(unitSkillSets[unitLesson.skill_id]?.lookfors || []).map((lf) => (
                  <option key={lf.number} value={lf.number}>{lf.name}</option>
                ))}
              </select>
            </>
          )}
          {savingSkill && <Loader2 className="h-3 w-3 animate-spin text-[#1c2d3d]/50" />}
        </div>
      )}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button
          type="button"
          onClick={handleGenerate}
          disabled={busy}
          data-testid={`unit-lesson-generate-${lessonIndex}`}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md bg-[#1c2d3d] text-white font-bold text-sm hover:bg-[#0f1c28] disabled:opacity-60 disabled:cursor-wait"
        >
          {busy ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Drafting this lesson…
            </>
          ) : (
            <>
              <Sparkles className="h-4 w-4" />
              Generate this lesson
            </>
          )}
        </button>
        <button
          type="button"
          onClick={() => setShowPrompt((v) => !v)}
          data-testid={`unit-lesson-view-prompt-${lessonIndex}`}
          className="inline-flex items-center gap-1.5 text-[#1c2d3d]/70 hover:text-[#1c2d3d] font-semibold text-xs"
        >
          {showPrompt ? (
            <>
              <ChevronUp className="h-3 w-3" /> Hide prompt
            </>
          ) : (
            <>
              <ChevronDown className="h-3 w-3" /> View / edit prompt
            </>
          )}
        </button>
      </div>
      {showPrompt && (
        <div className="mt-3 space-y-2">
          <textarea
            value={editedPrompt}
            onChange={(e) => setEditedPrompt(e.target.value)}
            disabled={busy}
            rows={4}
            data-testid={`unit-lesson-prompt-textarea-${lessonIndex}`}
            className="w-full rounded-md border border-[#1c2d3d]/20 bg-white p-3 text-[#1c2d3d] text-sm leading-relaxed font-medium focus:outline-none focus:ring-2 focus:ring-[#96ded1]"
          />
        </div>
      )}
    </div>
  );
};

// Owns per-lesson generation state for ALL lessons in the unit so the
// top-of-section "Generate all remaining" button can drive them
// sequentially. Each child card is still individually clickable.
// One-click bulk-archival of every drafted lesson in a unit into a
// single Drive sub-folder named after the unit. Only renders when the
// teacher has Drive connected AND at least one lesson has been drafted.
const SaveUnitToDriveButton = ({ unitId, hasAnyGenerated }) => {
  const [drive, setDrive] = useState(null);
  const [busy, setBusy] = useState(null);
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    driveStatus()
      .then((s) => {
        if (!cancelled) setDrive(s);
      })
      .catch(() => {
        if (!cancelled) setDrive({ configured: false, connected: false });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  if (!unitId || !hasAnyGenerated) return null;
  if (!drive?.configured || !drive?.connected) return null;

  const handleSave = async (format, label) => {
    if (busy) return;
    setBusy(format);
    setOpen(false);
    const toastId = toast.loading(
      `Saving every drafted lesson to Drive as ${label}…`,
      { description: "Creating one folder per unit. About 10 seconds per lesson." },
    );
    try {
      const res = await saveUnitToDrive(unitId, format);
      const savedCount = res.saved?.length || 0;
      const failedCount = res.failed?.length || 0;
      if (failedCount === 0) {
        toast.success(
          `Saved ${savedCount} ${savedCount === 1 ? "lesson" : "lessons"} to Drive`,
          {
            id: toastId,
            description: `Folder: ${res.folder_name}`,
            duration: 10000,
            action: res.folder_url
              ? {
                  label: "Open folder",
                  onClick: () =>
                    window.open(res.folder_url, "_blank", "noreferrer"),
                }
              : undefined,
          },
        );
      } else {
        toast.warning(
          `Saved ${savedCount}, ${failedCount} failed — open the folder and retry the missing ones individually.`,
          {
            id: toastId,
            duration: 12000,
            action: res.folder_url
              ? {
                  label: "Open folder",
                  onClick: () =>
                    window.open(res.folder_url, "_blank", "noreferrer"),
                }
              : undefined,
          },
        );
      }
    } catch (err) {
      const detail = err?.response?.data?.detail || err?.message || "Drive save failed";
      toast.error("Couldn't save unit to Drive", {
        id: toastId,
        description: String(detail),
      });
    } finally {
      setBusy(null);
    }
  };

  const formats = [
    { fmt: "package", label: "Lesson Package PDFs", icon: FileText },
    { fmt: "gdoc", label: "Google Docs (Lesson Overview)", icon: FileType2 },
    { fmt: "pdf", label: "Lesson Overview PDFs", icon: FileText },
    { fmt: "docx", label: "Word (.docx) — Lesson Overview", icon: FileType2 },
  ];

  return (
    <div ref={wrapperRef} className="relative inline-flex">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        disabled={!!busy}
        data-testid="save-unit-to-drive"
        aria-haspopup="menu"
        aria-expanded={open}
        className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md border border-[#4a6984]/25 bg-white text-[#1c2d3d] font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#f4f5f7] disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {busy ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : (
          <Cloud className="h-3.5 w-3.5" />
        )}
        {busy ? "Saving…" : "Save unit to Drive"}
        <ChevronDown className="h-3 w-3 ml-0.5" />
      </button>
      {open && !busy && (
        <div
          role="menu"
          data-testid="save-unit-to-drive-menu"
          className="absolute left-0 top-full mt-2 z-30 min-w-[16rem] max-w-[calc(100vw-1.5rem)] rounded-md border border-[#4a6984]/15 bg-white shadow-lg overflow-hidden"
        >
          <div className="px-3.5 pt-2.5 pb-1 text-[10px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/60">
            Save every drafted lesson as…
          </div>
          {formats.map((f) => (
            <button
              key={f.fmt}
              type="button"
              role="menuitem"
              data-testid={`save-unit-to-drive-${f.fmt}`}
              onClick={() => handleSave(f.fmt, f.label)}
              className="w-full text-left px-3.5 py-2 text-sm font-medium text-[#1c2d3d] hover:bg-[#96ded1]/15 focus:bg-[#96ded1]/15 focus:outline-none inline-flex items-center gap-2"
            >
              <f.icon className="h-3.5 w-3.5 text-[#1c2d3d]/70" />
              {f.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const UnitLessonsSection = ({ lessons, unitInputs, unitId, unitLessons }) => {
  // Array of `{busy, generated}` keyed by lesson index — empty objects
  // for not-yet-touched lessons. On mount we hydrate from the backend
  // so previously-generated child lessons show up as "Open lesson"
  // cards even after the teacher closes and reopens the unit.
  const [lessonStates, setLessonStates] = useState(() =>
    lessons.map(() => ({ busy: false, generated: null })),
  );
  const [batchRunning, setBatchRunning] = useState(false);
  // units-on-the-store — the store look-for menu + this unit's structured
  // per-lesson records (editable), so the cards can show + change skill/focus.
  const [focusStore, setFocusStore] = useState(null);
  const [records, setRecords] = useState(() => unitLessons || []);
  useEffect(() => { setRecords(unitLessons || []); }, [unitLessons]);
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/skills/focus-areas`)
      .then((r) => (r.ok ? r.json() : null))
      .then(setFocusStore)
      .catch(() => setFocusStore(null));
  }, []);

  // The unit's CLOSED per-skill focus-area sets, resolved to display names.
  // { skill_id: { skill_name, lookfors: [{number, name}] } } — drives both the
  // card dropdowns and the constraint. Empty for legacy units (no picker shown).
  const unitSkillSets = useMemo(() => {
    const sel = (unitInputs?.unit_focus_areas) || [];
    if (!focusStore || sel.length === 0) return {};
    const out = {};
    for (const fa of sel) {
      const sid = fa?.skill_id;
      const store = sid && focusStore[sid];
      if (!store) continue;
      const nums = new Set((fa.lookfor_numbers || []).map(Number));
      const lookfors = (store.lookfors || []).filter((lf) => nums.has(lf.number));
      if (lookfors.length) out[sid] = { skill_name: store.skill_name, lookfors };
    }
    return out;
  }, [unitInputs, focusStore]);

  const recordFor = (authoredIndex) =>
    records.find((r) => r.index === authoredIndex) || null;

  const handleSkillChange = async (authoredIndex, patch) => {
    try {
      const res = await patchUnitLessonSkill(unitId, {
        lesson_index: authoredIndex,
        skill_id: patch.skill_id,
        focus_area_number: patch.focus_area_number,
      });
      setRecords((prev) =>
        prev.map((r) => (r.index === authoredIndex ? { ...r, ...(res.lesson || {}) } : r)),
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't update this lesson's skill.");
    }
  };

  useEffect(() => {
    if (!unitId) return undefined;
    let abort = false;
    (async () => {
      try {
        const children = await listUnitLessons(unitId);
        if (abort || !Array.isArray(children) || children.length === 0) return;
        setLessonStates((prev) => {
          const next = [...prev];
          for (const child of children) {
            const idx = child?.parent_lesson_index;
            if (typeof idx === "number" && idx >= 0 && idx < next.length) {
              next[idx] = { busy: false, generated: child };
            }
          }
          return next;
        });
      } catch {
        /* non-fatal — cards will just default to "Generate this lesson" */
      }
    })();
    return () => {
      abort = true;
    };
  }, [unitId]);

  const updateState = (i, patch) => {
    setLessonStates((prev) => {
      const next = [...prev];
      next[i] = { ...next[i], ...patch };
      return next;
    });
  };

  const remainingIndexes = lessons
    .map((lsn, i) =>
      lsn.prompt && !lessonStates[i]?.generated && !lessonStates[i]?.busy ? i : null,
    )
    .filter((x) => x !== null);
  const remainingCount = remainingIndexes.length;
  const totalPromptable = lessons.filter((l) => l.prompt).length;
  const generatedCount = lessonStates.filter((s) => s?.generated).length;

  const handleGenerateAll = async () => {
    if (batchRunning || remainingCount === 0) return;
    setBatchRunning(true);
    const toastId = toast.loading(
      `Drafting ${remainingCount} ${remainingCount === 1 ? "lesson" : "lessons"}…`,
      {
        description: "A minute or two per lesson. You can keep scrolling.",
      },
    );
    let done = 0;
    let failed = 0;
    let stillCooking = 0;
    // For each lesson the inner generate fails on with a "still cooking"
    // signal, we kick off background polling so the card auto-swaps to
    // the green generated state when Claude finishes — no page refresh
    // required.
    const resumePromises = [];
    // Re-read the latest state each iteration via the indexes computed
    // BEFORE we started — any in-flight individual clicks during the
    // batch get a `skipped` no-op because their `busy` flag is true.
    for (const i of remainingIndexes) {
      const lsn = lessons[i];
      if (!lsn.prompt) continue;
      updateState(i, { busy: true });
      let capturedJobId = null;
      const rec = recordFor(i + 1);
      const sel =
        rec && rec.skill_id && rec.focus_area_number
          ? [{ skill_id: rec.skill_id, lookfor_number: rec.focus_area_number }]
          : [];
      try {
        const activity = await generateActivity(
          {
            prompt: lsn.prompt,
            grade: unitInputs?.grade || "",
            subject: unitInputs?.subject || "",
            skill_focus: unitInputs?.skill_focus || "",
            time: unitInputs?.time || "",
            activity_type: "Full lesson",
            output_type: "lesson",
            parent_unit_id: unitId || undefined,
            parent_lesson_index: unitId ? i : undefined,
            skill_lookfor_selections: sel,
            lookfor_grade: unitInputs?.lookfor_grade || "",
          },
          { onJobStarted: (jid) => { capturedJobId = jid; } },
        );
        updateState(i, { busy: false, generated: activity });
        done += 1;
        toast.loading(
          `Drafted ${done} of ${remainingCount}…`,
          { id: toastId },
        );
      } catch (e) {
        if (isLikelyStillCooking(e) && capturedJobId) {
          // Keep this card in busy state and resume polling in the
          // background. When it finishes, the card flips to "generated".
          stillCooking += 1;
          resumePromises.push(
            resumeGenerateJob(capturedJobId)
              .then((activity) => {
                updateState(i, { busy: false, generated: activity });
                toast.success(`Lesson ${i + 1} landed in the background.`);
              })
              .catch(() => {
                updateState(i, { busy: false });
              }),
          );
        } else {
          updateState(i, { busy: false });
          if (isLikelyStillCooking(e)) {
            stillCooking += 1;
          } else {
            failed += 1;
          }
        }
      }
    }
    setBatchRunning(false);
    if (failed === 0 && stillCooking === 0) {
      toast.success(
        `All ${done} ${done === 1 ? "lesson" : "lessons"} drafted — saved to your library.`,
        { id: toastId },
      );
    } else if (done === 0 && failed === 0 && stillCooking > 0) {
      toast.info(STILL_COOKING_TITLE, {
        id: toastId,
        description: STILL_COOKING_BODY,
        duration: 12000,
        action: {
          label: "Open Library",
          onClick: () => {
            window.location.href = "/dashboard?tab=library";
          },
        },
      });
    } else if (done === 0) {
      toast.error("Couldn't draft any of the remaining lessons.", { id: toastId });
    } else if (failed === 0 && stillCooking > 0) {
      toast.info(
        `Drafted ${done} on screen — ${stillCooking} ${stillCooking === 1 ? "is" : "are"} still cooking. ${stillCooking === 1 ? "It" : "They"} will pop in automatically when ready.`,
        {
          id: toastId,
          duration: 12000,
          action: {
            label: "Open Library",
            onClick: () => {
              window.location.href = "/dashboard?tab=library";
            },
          },
        },
      );
    } else {
      toast.warning(
        `Drafted ${done}, ${failed} failed${stillCooking ? `, ${stillCooking} still cooking (will pop in)` : ""}. Click the failed lessons to retry.`,
        { id: toastId },
      );
    }
  };

  return (
    <section data-testid="unit-lessons">
      <div className="mt-10 pb-2 border-b border-[#4a6984]/15 flex items-end justify-between gap-3 flex-wrap">
        <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight">
          Lesson-by-Lesson Overview
        </h2>
        {totalPromptable > 0 && (
          <div
            className="flex items-center gap-3 flex-wrap"
            data-testid="unit-generate-all-row"
          >
            <span className="text-xs font-bold uppercase tracking-[0.14em] text-[#4a6984]/70">
              {generatedCount}/{totalPromptable} drafted
            </span>
            <SaveUnitToDriveButton
              unitId={unitId}
              hasAnyGenerated={generatedCount > 0}
            />
            <button
              type="button"
              onClick={handleGenerateAll}
              disabled={batchRunning || remainingCount === 0}
              data-testid="unit-generate-all"
              className="inline-flex items-center gap-2 px-3.5 py-2 rounded-md bg-[#1c2d3d] text-white font-bold text-xs uppercase tracking-[0.1em] hover:bg-[#0f1c28] disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {batchRunning ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" /> Drafting…
                </>
              ) : remainingCount === 0 ? (
                <>
                  <Check className="h-3.5 w-3.5" /> All drafted
                </>
              ) : (
                <>
                  <Layers className="h-3.5 w-3.5" />
                  Generate {remainingCount === totalPromptable ? "all" : remainingCount === 1 ? "the remaining" : `${remainingCount} remaining`}
                </>
              )}
            </button>
          </div>
        )}
      </div>
      <div className="mt-5 space-y-5">
        {lessons.map((lsn, i) => (
          <div
            key={i}
            data-testid={`unit-lesson-${i}`}
            className="rounded-xl border border-[#4a6984]/15 bg-[#f4f5f7]/50 p-5 md:p-6"
          >
            <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-2">
              {lsn.title}
            </h3>
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {lsn.body}
            </ReactMarkdown>
            <GenerateLessonCard
              prompt={lsn.prompt}
              lessonIndex={i}
              unitInputs={unitInputs}
              parentUnitId={unitId}
              unitLesson={recordFor(i + 1)}
              focusStore={focusStore}
              unitSkillSets={unitSkillSets}
              onSkillChange={handleSkillChange}
              status={lessonStates[i]}
              onStatusChange={(patch) => updateState(i, patch)}
            />
          </div>
        ))}
      </div>
    </section>
  );
};

export const UnitDocument = ({ activity, onActivityUpdate, enableRefine = true, bare = false }) => {
  // Skill pills come from the canonical transferable_skills array (one pill per
  // skill). Fall back to the joined skill_focus string ONLY when the array is
  // absent (legacy single-skill units). Never render both — that duplicated the
  // pill as a combined string alongside the per-skill pills (Jun 2026 defect).
  const skillTags =
    activity?.transferable_skills && activity.transferable_skills.length
      ? activity.transferable_skills
      : activity?.inputs?.skill_focus
        ? [activity.inputs.skill_focus]
        : [];
  const tags = [
    ...skillTags,
    ...(activity?.ontario_learning_skills || []),
  ];
  const chips = meta(activity?.inputs, activity?.lesson_count);
  const md = activity?.unit_markdown || activity?.lesson_markdown || "";
  const { preface, lessons, suffix } = useMemo(() => splitUnit(md), [md]);

  // Phase 114.D.2 — fetch the unit's generated child lessons at the
  // parent level so the Distributed Planner inside UnitSummativeSection
  // gets a real `activity_id` per lesson row. Without this, the planner
  // would save with empty lesson_activity_ids and the LessonWorkspace
  // "Task component" badge would never appear on the child lesson page.
  const [childByIndex, setChildByIndex] = useState({});
  const [unaddressed, setUnaddressed] = useState([]);
  useEffect(() => {
    // Surface, in the FINISHED unit, any teacher-selected expectations the
    // coverage plan intentionally did NOT teach — so a gap is visible weeks
    // later, not only on the pre-generation card. Reads the linked breakdown.
    const bid = activity?.inputs?.skills_breakdown_id;
    if (!bid) { setUnaddressed([]); return undefined; }
    let abort = false;
    (async () => {
      try {
        const res = await getSkillsBreakdown(bid);
        if (abort) return;
        setUnaddressed(res?.summary?.unaddressed_expectations || []);
      } catch {
        /* non-fatal — no note shown if the plan can't be read */
      }
    })();
    return () => { abort = true; };
  }, [activity?.inputs?.skills_breakdown_id]);
  useEffect(() => {
    if (!activity?.id) return undefined;
    let abort = false;
    (async () => {
      try {
        const children = await listUnitLessons(activity.id);
        if (abort || !Array.isArray(children)) return;
        const map = {};
        for (const c of children) {
          if (typeof c?.parent_lesson_index === "number" && c.id) {
            map[c.parent_lesson_index] = c.id;
          }
        }
        setChildByIndex(map);
      } catch {
        /* non-fatal — distributed planner just falls back to empty ids */
      }
    })();
    return () => {
      abort = true;
    };
  }, [activity?.id]);

  if (!activity) return null;

  // Phase 114.D.1 — Summative Assessment fork. Build the JSX once so
  // both the bare (LessonWorkspace /lesson/:id) and non-bare
  // (Generator just-generated view) render paths surface the same
  // section. Without this both branches drift in feature coverage.
  const summativeSection = activity?.id ? (
    <UnitSummativeSection
      unitActivityId={activity.id}
      unitTitle={activity.activity_name || activity.title || ""}
      requiresSummative={!!activity?.inputs?.requires_summative_model}
      unitContext={{
        subject: activity.inputs?.subject || "",
        grade: activity.inputs?.grade || "",
        // Strand letter — derive from the first curriculum_link. The
        // unit may carry multiple strands; we pin to the primary so the
        // Summative Task Builder's strand dropdown lands on a real
        // value. Multi-strand units can override inside the Builder.
        strand: (() => {
          const codes = activity.curriculum_links || activity.expectations || [];
          const first = codes.find((c) => /^[A-Za-z]/.test(c || ""));
          return first ? first[0].toUpperCase() : "";
        })(),
        big_idea: (() => {
          // Prefer the verbatim stored big idea (canonical). Fall back to
          // parsing the preface, tolerating the "## The Big Idea" heading the
          // render transform injects (the old regex only matched "## Big Idea").
          if (activity.big_idea && activity.big_idea.trim()) return activity.big_idea.trim();
          if (activity.inputs?.big_idea && String(activity.inputs.big_idea).trim())
            return String(activity.inputs.big_idea).trim();
          const m = (preface || "").match(
            /##\s+(?:The\s+)?Big\s+Idea\s*\n+([\s\S]*?)(?:\n##\s+|$)/i,
          );
          return (m?.[1] || "").trim();
        })(),
        lesson_count: lessons.length,
        lessons: lessons.map((l, idx) => ({
          lesson_title: l.title || l.lesson_title || `Lesson ${idx + 1}`,
          activity_id: childByIndex[idx] || l.id || l.activity_id || "",
        })),
        curriculum_codes: activity.curriculum_links || activity.expectations || [],
        reporting_period: activity.inputs?.reporting_period || "",
      }}
    />
  ) : null;

  // Phase 101 — per-lesson Student Sheet PDF bundles. Built once so BOTH the
  // bare (LessonWorkspace) and non-bare render paths surface it (previously
  // only the non-bare branch did, so units opened at /lesson/:id had no
  // per-lesson materials surface at all). Each row renders only when it has
  // sheets, and passes its own lesson_index — so no button ever promises a
  // download that 404s (§3 fail-loud).
  const studentMaterialsSection =
    Array.isArray(activity.student_sheets) && activity.student_sheets.length > 0 ? (
      <section
        data-testid="unit-student-materials"
        className="mt-10 pt-10 border-t-2 border-dashed border-[#96ded1]/60"
      >
        <h2 className="font-extrabold text-2xl text-[#4a6984] tracking-tight mb-6">
          Student Materials by Lesson
        </h2>
        <div className="space-y-4">
          {activity.student_sheets
            .filter((row) => row && Array.isArray(row.sheets) && row.sheets.length > 0)
            .map((row) => (
              <StudentMaterialsSection
                key={row.lesson_index}
                activity={activity}
                lessonIndex={row.lesson_index}
                lessonTitle={row.lesson_title || `Lesson ${row.lesson_index}`}
              />
            ))}
        </div>
      </section>
    ) : null;

  // Honest gap note — teacher-selected expectations the plan did not teach.
  const unaddressedSection =
    unaddressed && unaddressed.length > 0 ? (
      <div
        data-testid="unit-unaddressed-note"
        className="mt-6 flex items-start gap-2 rounded-xl border border-[#b45309]/25 bg-[#fffbeb] p-4"
      >
        <FileText className="h-4 w-4 shrink-0 text-[#b45309] mt-0.5" />
        <p className="text-sm text-[#7c5310]">
          <span className="font-bold">Coverage note:</span> {unaddressed.length} expectation
          {unaddressed.length === 1 ? "" : "s"} you selected ({unaddressed.join(", ")}) weren&apos;t
          required by this unit&apos;s task, so they&apos;re intentionally not taught here. Plan them into
          another unit so they don&apos;t slip through.
        </p>
      </div>
    ) : null;

  // Bare mode — used by LessonWorkspace, which already provides the page
  // header, tag pills, and export bar. We skip the outer article frame +
  // header + export buttons and emit just the unit-document body so the
  // per-lesson "Generate this lesson" buttons appear inside the tabbed
  // workspace surface.
  if (bare) {
    return (
      <div data-testid="unit-document-bare" className="ae-prose">
        {preface && (
          <div data-testid="unit-preface">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {preface}
            </ReactMarkdown>
          </div>
        )}
        {unaddressedSection}
        {lessons.length > 0 && (
          <UnitLessonsSection lessons={lessons} unitInputs={activity.inputs} unitId={activity.id} unitLessons={activity.unit_lessons} />
        )}
        {summativeSection}
        {studentMaterialsSection}
        {suffix && (
          <div data-testid="unit-suffix">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {suffix}
            </ReactMarkdown>
          </div>
        )}
      </div>
    );
  }

  return (
    <article
      data-testid="unit-document"
      className="ae-card shadow-xl shadow-[#4a6984]/5 p-6 md:p-12"
    >
      <header className="border-b border-[#4a6984]/10 pb-8 mb-6">
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div className="min-w-0">
            <p className="font-bold uppercase tracking-[0.25em] text-xs text-[#4a6984]/70">
              Unit Plan
              {activity.lesson_count
                ? ` · ${activity.lesson_count} ${activity.lesson_count === 1 ? "lesson" : "lessons"}`
                : ""}
            </p>
            <h2
              data-testid="activity-name"
              className="font-black text-3xl sm:text-4xl text-[#4a6984] mt-3 tracking-tight"
            >
              {activity.activity_name || "Untitled Unit"}
            </h2>
            {activity.tagline && (
              <p
                data-testid="activity-tagline"
                className="mt-3 text-[#4a6984]/85 font-medium text-base sm:text-lg max-w-2xl"
              >
                {activity.tagline}
              </p>
            )}
          </div>
          {activity.id && (
            <div className="shrink-0">
              <p className="font-bold uppercase text-[10px] tracking-[0.25em] text-[#4a6984]/60 mb-2">
                Export
              </p>
              <div className="flex flex-wrap items-center gap-2">
                <ExportButtons
                  activityId={activity.id}
                  activityName={activity.activity_name}
                  isUnit={activity.output_type === "unit"}
                  hasScript={!!activity.teacher_script_markdown}
                  hasHandout={
                    !activity.output_type?.startsWith("math_") &&
                    /Student Handout|Exit Ticket/i.test(activity.lesson_markdown || activity.unit_markdown || "")
                  }
                  size="sm"
                />
                <CopyLessonButton activity={activity} size="sm" testid="unit-copy" />
              </div>
            </div>
          )}
        </div>

        {(chips.length > 0 || tags.length > 0) && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="unit-chips">
            {chips.map((c) => (
              <span key={c.key} className="ae-pill">
                {c.value}
              </span>
            ))}
            {tags.slice(0, 8).map((t, i) => (
              <span
                key={`${t}-${i}`}
                className="ae-pill !bg-[#96ded1]/25 !border-[#96ded1]/60 !text-[#1c2d3d]"
                data-testid={`unit-tag-${i}`}
              >
                {t}
              </span>
            ))}
          </div>
        )}
      </header>

      <div className="ae-prose">
        {/* Preface (h1, front-matter, Big Idea, Curriculum, Learning Skills) */}
        {preface && (
          <div data-testid="unit-preface">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {preface}
            </ReactMarkdown>
          </div>
        )}

        {unaddressedSection}

        {/* Lesson-by-lesson — each gets its own card with Generate + Copy
            Prompt buttons, and the top of the section has a "Generate all
            remaining" batch button driven by per-lesson status. */}
        {lessons.length > 0 && (
          <UnitLessonsSection lessons={lessons} unitInputs={activity.inputs} unitId={activity.id} unitLessons={activity.unit_lessons} />
        )}

        {/* Phase 114.D.1 — Summative Assessment fork (Model A + scaffolding) */}
        {summativeSection}

        {/* Suffix (Differentiation, Summative, Report Card, Checklist) */}
        {suffix && (
          <div data-testid="unit-suffix">
            <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
              {suffix}
            </ReactMarkdown>
          </div>
        )}
      </div>

      {/* Phase 101 — per-lesson Student Sheet PDF bundles (shared const). */}
      {studentMaterialsSection}

      {enableRefine && activity.id && (
        <LessonRefineChat activity={activity} onActivityUpdate={onActivityUpdate} />
      )}
    </article>
  );
};

export default UnitDocument;
